<script lang="ts">
import type { VariantProps } from 'tailwind-variants';
import type { PropType, VNode } from 'vue';
import type { AnyItem, DisplayTemplate, DisplayTemplateAst, GlobalCollectionId, GlobalFieldId, RefNode, TextNode } from '#layers/data-model';
import type { AnyResolvedField, ResolvedMonospaceCollection } from '#layers/data-model/types/schema/resolved-schema';
import type { ThemeSlots } from '#layers/ui';
import { tv } from 'tailwind-variants';
import { computed, defineComponent, h } from 'vue';
import RenderFieldDisplay from './RenderFieldDisplay.vue';

const renderDisplayTemplate = tv({
	slots: {
		root: 'flex min-w-0 items-center overflow-hidden leading-8',
		content: 'flex min-w-0 items-center overflow-hidden',
	},
	variants: {
		layout: {
			'fill': {
				root: '-mx-2.5 flex-1 px-2.5',
				content: '-mx-2.5 flex-1 px-2.5',
			},
			'fit-content': {
				root: 'w-max max-w-full flex-none',
				content: 'w-max max-w-full flex-none',
			},
		},
	},
	defaultVariants: {
		layout: 'fill',
	},
});

type RenderDisplayTemplateVariants = VariantProps<typeof renderDisplayTemplate>;

export interface RenderDisplayTemplateProps<T extends AnyItem> {
	item: T;
	displayTemplate?: DisplayTemplate | null;
	collectionId?: GlobalCollectionId;
	class?: any;
	/**
	 * Fill normal display cells by default, or size to content for inline affordances that reserve their own trailing space.
	 */
	layout?: RenderDisplayTemplateVariants['layout'];
	/**
	 * Override display template slot classes.
	 */
	ui?: ThemeSlots<typeof renderDisplayTemplate>;
	/**
	 * Set to true if this is the root node of a display template.
	 * This can change the behavior of rendered content, e.g., related items might not show inline links
	 */
	root?: boolean;
}

type ResolvedRefNode = RefNode & { value: any; leafItem: AnyItem };
type RenderableNode =
	| { text: string }
	| { node: ResolvedRefNode };

function getLeafFieldId(fieldId: GlobalFieldId | GlobalFieldId[]): GlobalFieldId {
	return Array.isArray(fieldId) ? fieldId.at(-1)! : fieldId;
}

function getTextNodeText(node: TextNode, index: number, nodeCount: number): string {
	let text = node.text;

	if (index === 0) {
		text = text.trimStart();
	}

	if (index === nodeCount - 1) {
		text = text.trimEnd();
	}

	return text;
}

const RenderPriorityNodes = defineComponent({
	name: 'RenderDisplayTemplatePriorityNodes',
	props: {
		nodes: {
			type: Array as PropType<RenderableNode[]>,
			required: true,
		},
		root: {
			type: Boolean,
			required: true,
		},
		allowRootFieldBleed: {
			type: Boolean,
			required: true,
		},
	},
	setup(componentProps) {
		return () => renderPriorityNodes(componentProps.nodes, componentProps.root, componentProps.allowRootFieldBleed);
	},
});

function renderPriorityNodes(renderNodes: RenderableNode[], root: boolean, allowRootFieldBleed: boolean, renderIndex = 0): VNode[] {
	const renderableNode = renderNodes[renderIndex];
	if (!renderableNode) return [];

	const isLast = renderIndex === renderNodes.length - 1;
	const isRootField = allowRootFieldBleed && root && renderNodes.length === 1;
	const renderedNodes = [renderToken(renderableNode, isLast, isRootField)];

	if (!isLast) {
		renderedNodes.push(h('span', { class: 'flex min-w-0 flex-auto items-center overflow-hidden' }, renderPriorityNodes(renderNodes, root, allowRootFieldBleed, renderIndex + 1)));
	}

	return renderedNodes;
}

function renderToken(renderableNode: RenderableNode, isLast: boolean, isRootField: boolean): VNode {
	const nodeClass = isLast ? 'min-w-0 flex-auto' : 'max-w-full min-w-0 shrink-0';

	if ('text' in renderableNode) {
		return h('span', {
			'class': `${nodeClass} overflow-hidden whitespace-pre`,
			'data-test-id': 'display-template-text-node',
		}, renderableNode.text);
	}

	const { node } = renderableNode;
	// The host owns vertical centering: this wrapper is a flex item, so its child is
	// blockified and centered by the row's `items-center` no matter what the block
	// renders (inline badge, icon chip, thumbnail). A flex box can't `text-overflow`,
	// so we only clip here; blocks ellipsize their own text.
	const boxClass = 'flex items-center overflow-hidden rounded-md max-h-lh';
	const nodeClassWithBleed = isRootField ? `${nodeClass} -mx-2.5 ${boxClass} px-2.5` : `${nodeClass} ${boxClass}`;

	return h('span', { class: nodeClassWithBleed }, [
		h(RenderFieldDisplay, {
			fieldId: getLeafFieldId(node.attrs.fieldId),
			value: node.value,
			item: node.leafItem,
			root: isRootField,
		}),
	]);
}
</script>

<script setup lang="ts" generic="T extends AnyItem =AnyItem">
import { useI18n } from 'vue-i18n';
import { ensureArray } from '#layers/base/utils/array';
import { isNullishOrEmpty } from '#layers/base/utils/iterable';
import { get } from '#layers/base/utils/object';
import { useCollection } from '#layers/data-model';
import { isEmptyDisplayTemplate, toDisplayTemplateAst } from '#layers/data-model/utils/display-template';
import { findCollectionFieldById } from '#layers/data-model/utils/fields/fields';
import { isSingleRelationField } from '#layers/data-model/utils/schema/collection';

const props = withDefaults(defineProps<RenderDisplayTemplateProps<T>>(), {
	layout: 'fill',
	root: true,
});

const { t } = useI18n();

const { collection } = useCollection(props.collectionId);
const renderableNodes = useRenderableTemplateNodes();

const configurationError = computed(() => !props.displayTemplate || isNullishOrEmpty(renderableNodes.value) ? t('display_template.no_display_template') : undefined);
const ui = computed(() => renderDisplayTemplate({ layout: props.layout }));

function resolveFieldIdPath(fieldIds: GlobalFieldId[], parentField: AnyResolvedField): string[] {
	const path: string[] = [];
	let currentCol: ResolvedMonospaceCollection | undefined = isSingleRelationField(parentField) ? parentField.oppositeCollection : undefined;

	for (const id of fieldIds) {
		if (!currentCol) break;
		const field = findCollectionFieldById(currentCol, id);
		if (!field) break;
		path.push(field.apiName);
		currentCol = isSingleRelationField(field) ? field.oppositeCollection : undefined;
	}

	return path;
}

function useRenderableTemplateNodes() {
	const ast = computed<DisplayTemplateAst | null>(() => {
		const { displayTemplate } = props;
		if (isEmptyDisplayTemplate(displayTemplate)) return null;
		if (!collection.value) return null;
		return toDisplayTemplateAst(displayTemplate, collection.value);
	});

	return computed<RenderableNode[]>(() => {
		if (!ast.value) return [];
		return getRenderableNodes(ast.value);
	});
}

function getRenderableNodes(ast: DisplayTemplateAst): RenderableNode[] {
	if (!collection.value) {
		console.warn('[RenderDisplayTemplate]: Collection is not defined.');
		return [];
	}

	const content = ast.content ?? [];

	return content.reduce<RenderableNode[]>((result, node, nodeIndex) => {
		if (node.type === 'text') {
			const text = getTextNodeText(node, nodeIndex, content.length);
			if (text) result.push({ text });
		}
		else {
			const resolvedNode = resolveRefNode(node);
			if (resolvedNode) result.push({ node: resolvedNode });
		}

		return result;
	}, []);
}

function resolveRefNode(node: RefNode): ResolvedRefNode | undefined {
	const fieldIds = ensureArray(node.attrs.fieldId);
	const field = findCollectionFieldById(collection.value!, fieldIds[0]!);

	if (!field) {
		console.warn(`[RenderDisplayTemplate]: Could not find field with id ${fieldIds[0]}.`);
	}

	if (!field || !props.item) {
		return undefined;
	}

	const rootValue = props.item[field.apiName];

	if (fieldIds.length <= 1) {
		return { ...node, value: rootValue, leafItem: props.item } as ResolvedRefNode;
	}

	if (rootValue == null || typeof rootValue !== 'object') {
		return undefined;
	}

	const remainingIds = fieldIds.slice(1);
	const nestedPath = resolveFieldIdPath(remainingIds, field);

	if (nestedPath.length !== remainingIds.length) {
		return undefined;
	}

	const parentPath = nestedPath.slice(0, -1);
	const leafItem = parentPath.length > 0 ? get(rootValue, parentPath) : rootValue;

	if (leafItem == null || typeof leafItem !== 'object') {
		return undefined;
	}

	return {
		...node,
		value: get(rootValue, nestedPath),
		leafItem,
	} as ResolvedRefNode;
}
</script>

<template>
	<div :class="ui.root({ class: [props.ui?.root, props.class] })">
		<DisplayTemplateConfigurationError v-if="configurationError" :error="configurationError" />
		<div v-else :class="ui.content({ class: props.ui?.content })">
			<RenderPriorityNodes :nodes="renderableNodes" :root="props.root" :allow-root-field-bleed="props.layout === 'fill'" />
		</div>
	</div>
</template>
